import React, { createContext, useContext } from "react";
import styled, { css } from "styled-components";

import {
  border,
  brand,
  radius,
  spacing,
  surface,
  text,
  typography,
} from "../../theme/tokens";

/* ------------------------------------------------------------------ */
/* Folder/file conflict modal radio group                              */
/* ------------------------------------------------------------------ */
/*
 * Historically these wrapped MUI's `RadioGroup` / `Radio` /
 * `FormControlLabel`. They are now native radio inputs styled via
 * tokens + a small internal context to replace MUI's group-coordination
 * behavior. Implementing this without a UI-library dependency avoids
 * adding new antd mocks to every test that transitively imports
 * `TabViewStyled.js`.
 *
 * Preferred API:
 *   <StyledRadioGroup name="..." value={x} onChange={(e) => ...}>
 *     <StyledRadio value="a">Label A</StyledRadio>
 *     <StyledRadio value="b">Label B</StyledRadio>
 *   </StyledRadioGroup>
 *
 * `onChange` is called with `{ target: { value } }` so existing MUI-era
 * handlers that read `e.target.value` keep working unchanged.
 *
 * `StyledFormControlLabel` is a back-compat shim that accepts the old
 * MUI-shaped `value` + `label` (+ ignored `control`) props and renders a
 * single `StyledRadio`, so historical call-sites keep compiling until
 * they migrate to the new API.
 */

const RadioGroupContext = createContext({
  name: undefined,
  value: undefined,
  onChange: undefined,
});

const RadioGroupRoot = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing[2]};
`;

export const StyledRadioGroup = ({
  name,
  value,
  onChange,
  children,
  ...rest
}) => (
  <RadioGroupContext.Provider value={{ name, value, onChange }}>
    <RadioGroupRoot role="radiogroup" {...rest}>
      {children}
    </RadioGroupRoot>
  </RadioGroupContext.Provider>
);

const RadioLabel = styled.label`
  display: inline-flex;
  align-items: center;
  gap: ${spacing[1.5]};
  font-family: ${typography.fontFamily};
  font-size: ${typography.size.base};
  color: ${text.tertiary};
  cursor: pointer;
  user-select: none;

  input[type="radio"] {
    accent-color: ${brand.primary};
    width: 16px;
    height: 16px;
    margin: 0;
    cursor: pointer;
  }

  input[type="radio"]:focus-visible {
    outline: 2px solid ${border.focus};
    outline-offset: 2px;
    border-radius: ${radius.pill};
  }
`;

export const StyledRadio = ({ value, children, ...rest }) => {
  const ctx = useContext(RadioGroupContext);
  const checked = ctx.value !== undefined ? ctx.value === value : undefined;
  const handleChange = (e) => {
    if (ctx.onChange) {
      ctx.onChange({ target: { value, name: ctx.name }, nativeEvent: e });
    }
  };
  return (
    <RadioLabel>
      <input
        type="radio"
        name={ctx.name}
        value={value}
        checked={checked}
        onChange={handleChange}
        {...rest}
      />
      {children}
    </RadioLabel>
  );
};

export const StyledFormControlLabel = ({
  value,
  label,
  // eslint-disable-next-line no-unused-vars
  control,
  ...rest
}) => (
  <StyledRadio value={value} {...rest}>
    {label}
  </StyledRadio>
);

/* ------------------------------------------------------------------ */
/* Layout: tab strip + page wrappers                                   */
/* ------------------------------------------------------------------ */

export const TabViewWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 100%;
  overflow: hidden;
  box-sizing: border-box;
  min-width: 0;

  > * {
    max-width: 100%;
    box-sizing: border-box;
  }
`;

export const TabSectionContainer = styled.div`
  display: flex;
  width: 100%;
  overflow: hidden;

  &::-webkit-scrollbar {
    height: 6px;
  }
  &::-webkit-scrollbar-track {
    background: ${surface.hover};
  }
  &::-webkit-scrollbar-thumb {
    background: ${text.disabled};
    border-radius: ${radius.sm};
  }
`;

export const TabHeaderWrapper = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  max-width: 100%;
  overflow: hidden;
  min-width: 0;
  box-sizing: border-box;
  position: relative;
`;

/* ------------------------------------------------------------------ */
/* TabItem                                                             */
/*                                                                     */
/* Active-tab treatment is Chrome/Acrobat-ish: the active tab sits on  */
/* a white page, the inactive tabs sit on the page-tint surface, and a */
/* 2px brand-primary bar at the top edge of the active tab gives the   */
/* "you are here" indicator without relying on opacity tricks.         */
/*                                                                     */
/* Known-bug fix: the previous `&:hover { opacity: "100%" }` emitted a */
/* literal quoted string into CSS which browsers reject, so inactive   */
/* tabs stayed at 50% opacity on hover. Now: hover always lifts to     */
/* full opacity with a subtle surface color shift.                     */
/* ------------------------------------------------------------------ */
export const TabItem = styled.div`
  padding: ${(props) => (props.collapsebtn ? "11px" : "11px 17px")};
  margin-right: ${spacing[1]};
  width: ${(props) => (props.collapsebtn ? "48px" : "auto")};
  flex: ${(props) => (props.collapsebtn ? "0 0 48px" : "1 1 0")};
  min-width: ${(props) => (props.collapsebtn ? "48px" : "60px")};
  border-top-right-radius: ${radius.md};
  border-top-left-radius: ${radius.md};
  cursor: pointer;
  opacity: ${(props) => (props.tabbackground ? 1 : 0.6)};
  /*
   * Active-tab indicator is gated on $isActive (transient prop), NOT
   * tabbackground. getTabBackground() returns "true" for both the
   * active tab *and* the collapse button (tab id "0"), so keying the
   * 2px brand bar off tabbackground would paint the indicator on the
   * collapse button permanently regardless of which document tab is
   * actually active. $isActive is strict equality (activeTab === tab.id)
   * so the collapse tab only lights up when it is genuinely the
   * focused tab.
   *
   * Both states use a 2px top border — inactive tabs use
   * 2px solid transparent — so the active → inactive transition
   * only animates color, never width. Mismatched border widths
   * (2px active, 1px inactive) would shift tab-label content 1px
   * on every tab click.
   */
  border-top: 2px solid
    ${(props) => (props.$isActive ? brand.primary : "transparent")};
  box-shadow: ${(props) =>
    props.$isActive ? "none" : `inset 0 1px 0 ${border.default}`};
  border-right: 1px solid ${border.default};
  border-left: 1px solid ${border.default};
  border-bottom: ${(props) =>
    props.borderbottom ? "0px" : `1px solid ${border.default}`};
  background-color: ${(props) =>
    props.tabbackground ? surface.surface : surface.page};
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
  top: 2px;
  user-select: none;
  overflow: hidden;
  transition:
    background-color 0.15s ease,
    opacity 0.15s ease,
    border-top-color 0.15s ease;

  &:hover {
    opacity: 1;
    cursor: pointer;
    background-color: ${(props) =>
      props.tabbackground ? surface.surface : surface.hover};
  }

  &[draggable="true"] {
    transition:
      background-color 0.15s ease,
      opacity 0.15s ease,
      border-top-color 0.15s ease,
      transform 0.2s ease;
  }

  &[draggable="true"]:active,
  &[draggable="true"]:hover {
    cursor: pointer;
  }
`;

/* ------------------------------------------------------------------ */
/* Popovers                                                            */
/* ------------------------------------------------------------------ */
export const PopContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: ${spacing[4]};
`;

export const PopContainerItemWrapper = styled.div`
  display: flex;
  gap: ${spacing[2]};
  margin-bottom: ${spacing[1]};
`;

export const PopContainerLabel = styled.div`
  font-family: ${typography.fontFamily};
  font-weight: ${typography.weight.semibold};
  font-size: 0.875rem;
  line-height: 100%;
  letter-spacing: 0px;
  vertical-align: middle;
  padding: ${spacing[2]} 0;
  color: ${text.secondary};
`;

export const HorizondalLine = styled.hr`
  border: 1px solid ${border.default};
  height: 24px;
  margin: 0 ${spacing[2]};
`;

/*
 * The `.open` / `.closed` status chips inside PopContainerItem pre-date
 * the shared `StatusChip` component. Keeping the palette here pinned to
 * the same tokens `StatusChip` uses so the two surfaces stay visually
 * coherent until PopContainerItem is migrated to the shared component.
 */
const STATUS_READY_FG = "#16B361";
const STATUS_READY_BG = "#DDF6E9";
const STATUS_FAILED_FG = "#B31514";
const STATUS_FAILED_BG = "#F6DBDB";

export const PopContainerItem = styled.div`
  font-family: ${typography.fontFamily};
  display: flex;
  font-weight: ${typography.weight.medium};
  font-size: 0.688rem;
  line-height: 100%;
  letter-spacing: 0%;
  vertical-align: middle;
  color: ${text.tertiary};
  align-items: center;
  .open {
    margin-left: 2px;
    background: ${STATUS_READY_BG};
    padding: 4px 8px;
    border-radius: 18px;
    color: ${STATUS_READY_FG};
    width: fit-content;
  }
  .closed {
    margin-left: 2px;
    background: ${STATUS_FAILED_BG};
    padding: 4px 8px;
    border-radius: 18px;
    color: ${STATUS_FAILED_FG};
    width: fit-content;
  }
`;

/* ------------------------------------------------------------------ */
/* Text / labels                                                       */
/* ------------------------------------------------------------------ */

const labelTextBase = css`
  font-family: ${typography.fontFamily};
  line-height: 100%;
  letter-spacing: 0px;
  vertical-align: middle;
`;

export const TabLabel = styled.div`
  ${labelTextBase};
  display: flex;
  align-items: center;
  gap: ${spacing[1]};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-weight: ${typography.weight.semibold};
  font-size: ${typography.size.base};
  color: ${(props) => (props.tabactive ? brand.primary : text.muted)};
  width: ${(props) => (props.collapsebtn ? "fit-content" : "auto")};
  min-width: 0;
  flex: 1;
`;

export const TabContentArea = styled.div`
  transition: background 0.2s ease-in-out;
  background: ${({ isDraggingOverRow }) =>
    isDraggingOverRow
      ? `linear-gradient(
        to bottom,
        ${surface.surface} 70%,
        rgba(19, 67, 138, 0.3) 100%
      )`
      : surface.surface};
  padding: ${spacing[6]};
  flex: 1;
  border: 1px solid ${border.default};
  width: 100%;
  max-width: 100%;
  overflow: auto;
  box-sizing: border-box;
`;

export const DocumentContentArea = styled.div`
  background: ${surface.surface};
  flex: 1;
  height: calc(100vh - 54px);
  width: 100%;
  max-width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  box-sizing: border-box;
  padding: ${({ showActions }) => (showActions ? spacing[6] : "0")};
  border: ${({ showActions }) =>
    showActions ? `1px solid ${border.default}` : "none"};
  position: relative;
`;

export const TabContentHeaderWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px 12px;
  padding: 3px 2px;
  justify-content: space-between;
  align-items: center;
`;

export const DocumentContentHeaderWrapper = styled.div`
  display: flex;
  padding: 0;
  justify-content: space-between;
  align-items: anchor-center;
  margin-bottom: ${spacing[5]};
  position: relative;
  z-index: 20;
  background: ${surface.surface};
`;

export const ViewMSWrapper = styled.div`
  ${labelTextBase};
  font-size: ${typography.size.sm};
  font-weight: ${typography.weight.regular};
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
`;

/*
 * Toolbar action button for the open-document header.
 *
 * `$quiet` renders the low-emphasis outline treatment. The header carries at
 * most one filled button so the row reads as a toolbar rather than a wall of
 * competing primaries; everything else is quiet.
 */
export const ViewMSFileButton = styled.button`
  font-family: ${typography.fontFamily};
  font-size: ${typography.size.base};
  font-weight: ${typography.weight.medium};
  line-height: 1;
  white-space: nowrap;
  flex-shrink: 0;
  color: ${text.onAccent};
  text-decoration: none;
  background-color: ${brand.primary};
  border: 1px solid ${brand.primary};
  border-radius: ${radius.md};
  padding: 0 ${spacing[3]};
  height: 30px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease,
    color 0.15s ease;

  &:hover {
    background-color: ${brand.primaryHover};
    border-color: ${brand.primaryHover};
  }

  ${({ $quiet }) =>
    $quiet &&
    css`
      color: ${text.secondary};
      background-color: ${surface.surface};
      border-color: ${border.default};

      &:hover {
        color: ${text.primary};
        background-color: ${surface.hover};
        border-color: ${text.disabled};
      }
    `}

  &:disabled {
    background-color: ${surface.page};
    border-color: ${border.default};
    color: ${text.disabled};
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: 2px solid ${border.focus};
    outline-offset: 2px;
  }
`;

export const TabOuterWrapper = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: center;
`;

export const WarningLabel = styled.div`
  ${labelTextBase};
  font-weight: ${typography.weight.medium};
  font-size: 0.813rem;
  color: ${text.secondary};
`;

export const SpanName = styled.span`
  ${labelTextBase};
  font-weight: ${typography.weight.medium};
  font-size: 0.813rem;
  color: ${brand.primary};
`;

export const SpanBold = styled.span`
  ${labelTextBase};
  font-weight: ${typography.weight.bold};
  font-size: 0.813rem;
  color: ${text.secondary};
`;

export const FileNameShort = styled.span`
  display: inline-block;
  max-width: 150px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  vertical-align: bottom;
`;

export const TabNameContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[1]};
  max-width: 100%;
  min-width: 0;
  flex: 1;
`;

export const TabName = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: inline-block;
  flex: 1;
  min-width: 0;
  font-size: ${typography.size.sm};
`;

/* ------------------------------------------------------------------ */
/* Action + utility controls                                           */
/* ------------------------------------------------------------------ */

/*
 * Previously wrapped MUI's `Button`. Re-implemented as a plain styled
 * button so the last MUI surface in TabView drops, and so we can own
 * the focus-ring + disabled states without fighting MUI overrides.
 *
 * `.attrs({ type: "button" })` is required: native HTML `<button>`
 * defaults to `type="submit"`, so if this element ever ends up inside
 * a `<form>` ancestor, a click would trigger an unintended form
 * submission. MUI's `Button` defaulted to `type="button"` so the
 * legacy call-sites never set it explicitly.
 */
export const StyledActionButton = styled.button.attrs({ type: "button" })`
  font-family: ${typography.fontFamily};
  background-color: ${surface.surface};
  color: ${text.secondary};
  border: 1px solid ${border.default};
  padding: 2px 8px;
  font-size: ${typography.size.base};
  font-weight: ${typography.weight.semibold};
  text-transform: none;
  box-shadow: none;
  white-space: nowrap;
  min-width: auto;
  width: 50px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  top: 2px;
  border-radius: ${radius.md};
  cursor: pointer;
  transition:
    background-color 0.15s ease,
    border-color 0.15s ease;

  &:hover {
    background-color: ${surface.hover};
    border-color: ${border.light};
    box-shadow: none;
  }

  &:focus-visible {
    outline: 2px solid ${border.focus};
    outline-offset: 1px;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const FileConflictContent = styled.div`
  font-family: ${typography.fontFamily};
  font-size: ${typography.size.base};
  color: ${text.tertiary};
`;

export const TabSectionWrapper = styled.div`
  display: flex;
  align-items: center;
  flex: 1 1 0;
  min-width: 0;
  max-width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  position: relative;

  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;
`;

export const VisibleTabsContainer = styled.div`
  display: flex;
  flex: 1 1 auto;
  min-width: 0;
  width: 100%;
`;

export const TabDropdownContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  margin-left: ${spacing[1]};
`;

export const TabDropdownButton = styled.div`
  padding: 14px 16px;
  border-top-right-radius: ${radius.md};
  border-top-left-radius: ${radius.md};
  cursor: pointer;
  border-top: 1px solid ${border.default};
  border-right: 1px solid ${border.default};
  border-left: 1px solid ${border.default};
  border-bottom: 1px solid ${border.default};
  background-color: ${surface.surface};
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
  position: relative;
  top: 1px;
  user-select: none;
  font-family: ${typography.fontFamily};
  font-weight: ${typography.weight.semibold};
  font-size: ${typography.size.base};
  color: ${text.muted};
  min-width: 60px;
  justify-content: center;
`;

export const TabDropdownMenu = styled.div`
  position: absolute;
  top: -5px;
  background: ${surface.surface};
  border: 1px solid ${border.default};
  z-index: 1000;
  min-width: 280px;
  height: 95vh;
  overflow-y: auto;
  padding: ${spacing[2]} 0;
`;

export const TabDropdownItem = styled.div`
  padding: ${spacing[3]} ${spacing[4]};
  cursor: pointer;
  font-family: ${typography.fontFamily};
  font-weight: ${typography.weight.medium};
  font-size: ${typography.size.base};
  color: ${text.secondary};
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
  border-bottom: 1px solid ${border.light};
  position: relative;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: ${surface.hover};
  }

  &[draggable="true"] {
    cursor: pointer;

    &:active {
      cursor: pointer;
    }
  }
`;

export const TabDropdownItemContent = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
  flex: 1;
  overflow: hidden;
`;

export const TabDropdownItemTitle = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
`;

export const TabDropdownItemClose = styled.div`
  opacity: 0;
  transition: opacity 0.2s ease;
  cursor: pointer;
  padding: ${spacing[1]};
  border-radius: ${radius.sm};

  &:hover {
    background-color: ${border.light};
  }

  ${TabDropdownItem}:hover & {
    opacity: 1;
  }
`;

export const FixedActionsContainer = styled.div`
  display: flex;
  align-items: center;
  margin-left: auto;
  padding-right: ${spacing[1]};
  flex-shrink: 0;
  min-width: fit-content;
  position: absolute;
  z-index: 1;
  right: 0;
`;

export const FixedCollapseButtonContainer = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  min-width: fit-content;
  position: relative;
  z-index: 1;
  margin-right: ${spacing[2]};
`;

export const TabScrollButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 100%;
  border: 1px solid ${border.default};
  background-color: ${surface.surface};
  border-radius: ${radius.sm};
  cursor: pointer;
  padding: 0;
  flex-shrink: 0;
  z-index: 10;
  transition: all 0.2s ease;
  align-self: stretch;
  box-shadow: none;

  &:hover:not(:disabled) {
    background-color: ${surface.hover};
    border-color: ${border.light};
  }

  &:active:not(:disabled) {
    background-color: ${border.light};
  }

  &:focus-visible {
    outline: 2px solid ${border.focus};
    outline-offset: 1px;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
    background-color: ${surface.surface};

    &:hover {
      background-color: ${surface.surface};
      border-color: ${border.default};
    }

    svg {
      color: ${text.disabled};
    }
  }

  svg {
    width: 16px;
    height: 16px;
    color: ${text.tertiary};
    transition: color 0.2s ease;
  }
`;

export const TabScrollButtonLeft = styled(TabScrollButton)`
  margin-right: ${spacing[1]};
  flex-shrink: 0;
  position: relative;
  top: 2px;

  /* "There's more content on the right" affordance — subtle drop */
  /* shadow spilling off this button's right edge. */
  &:not(:disabled) {
    &::after {
      content: "";
      position: absolute;
      top: 0;
      bottom: 0;
      width: 40px;
      pointer-events: none;
      box-shadow: 6px 0 8px -2px rgba(0, 0, 0, 0.3);
    }
  }
`;

export const TabScrollButtonRight = styled(TabScrollButton)`
  margin-left: ${spacing[1]};
  margin-right: ${spacing[2]};
  flex-shrink: 0;
  position: relative;
  top: 2px;

  /* "There's more content on the left" affordance — mirror of the */
  /* left scroll button's shadow, spilling off this button's left edge. */
  &:not(:disabled) {
    &::before {
      content: "";
      position: absolute;
      top: 0;
      bottom: 0;
      width: 48px;
      box-shadow: -6px 0 8px -2px rgba(0, 0, 0, 0.3);
      pointer-events: none;
      z-index: -1;
    }
  }
`;

export const ScrollButtonIconWrapper = styled.div`
  border: 1px solid ${border.default};
  padding: 2px;
  display: flex;
  border-radius: ${radius.md};
`;
